import { Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { PostMetadata } from './post-metadata';
import { usePosts } from './posts.provider';

interface PostCardProps {
    post: PostMetadata,
    setCurrentPost: (post: PostMetadata) => void,
}

export function PostCard({ post, setCurrentPost }: PostCardProps) {
    const {postsBasePath} = usePosts();

    return (<div key={post.slug} className="bg-white border rounded-lg shadow-md overflow-hidden flex flex-col gap-4 transition-transform duration-300 hover:shadow-lg hover:-translate-y-1">
        <div className="relative h-[300px] w-full">
            <img
                src={post.image}
                alt={post.title}
                className="object-cover object-center h-[300px]"
                style={{ objectFit: 'cover', width: '100%', height: '100%' }}
            />
        </div>
        <div className="p-6 flex flex-col gap-4 flex-grow">
            <h3 className="text-2xl font-exo text-rose font-semibold mb-2 line-clamp-2">
                <Link
                    to={`${postsBasePath}/${post.slug}`}
                    className="hover:text-primary"
                    onClick={() => setCurrentPost(post)}
                >
                    {post.title}
                </Link>
            </h3>
            {/* Méthadonnées */}
            <p className="text-sm text-gray-500 mb-2">
                {post.date} {post.heure && `- ${post.heure}`}
            </p>
            <p className="text-normal text-gray-700 line-clamp-3 flex-grow">{post.description}</p>
            <div className="mt-4">
                <Link
                    to={`${postsBasePath}/${post.slug}`}
                    onClick={() => setCurrentPost(post)}
                >
                    <Button variant="rose" className="justify-between">
                        Consulter <ArrowRight size={16} />
                    </Button>
                </Link>
            </div>
        </div>
    </div>)
}


export function PostHoriezontalCard({ post, setCurrentPost }: PostCardProps) {
    const {postsBasePath} = usePosts();

    return (<div className="flex items-center gap-6">
        <div className="flex-shrink-0 w-20 h-20 relative">
            <img src={post.image} alt={post.title} className="object-cover object-center" style={{ objectFit: 'cover', width: '100%', height: '100%' }} />
        </div>
        <div className="flex-grow">
            <h3 className="text-xl font-exo text-rose font-semibold mb-2 line-clamp-2">
                <Link
                    to={`${postsBasePath}/${post.slug}`}
                    className="hover:text-primary"
                    onClick={() => setCurrentPost(post)}
                >
                    {post.title}
                </Link>
            </h3>
            <p className="text-sm text-gray-500 mb-2">
                {post.date} {post.heure && `- ${post.heure}`}
            </p>
        </div>
    </div>)
}


export function PostImageBgCard({post, setCurrentPost}: PostCardProps) {
    const {postsBasePath} = usePosts();

    return (
        <div className="relative overflow-hidden h-[572px] rounded-lg shadow-md group">
            <img
                src={post.image}
                alt={post.title}
                className="object-cover object-center"
                style={{ objectFit: 'cover', width: '100%', height: '100%' }}
            />
            <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-xs group-hover:backdrop-blur-lg"></div>

            <div className="absolute inset-0 p-6 flex flex-col gap-4 text-white justify-end mb-4 text-center">
                <h3 className="text-2xl font-exo font-semibold mb-2 line-clamp-2">{post.title}</h3>
                <p className="text-sm">
                    {post.date} {post.heure && `- ${post.heure}`}
                </p>
                <p className="text-normal text-white line-clamp-3">{post.description}</p>
                <div className="mt-4">
                    <Link
                        to={`${postsBasePath}/${post.slug}`}
                        onClick={() => setCurrentPost(post)}
                    >
                        <Button variant="yellow">Consulter</Button>
                    </Link>
                </div>
            </div>
        </div>
    )
}

export function SimilarPostCard({ post: post, setCurrentPost: setCurrentPost }: PostCardProps) {
    const {postsBasePath} = usePosts();

    return (
        <div className="bg-white border rounded-lg shadow-md overflow-hidden flex flex-col transition-transform duration-300 hover:shadow-lg hover:-translate-y-1">
            <div className="relative h-[200px] w-full">
                <img
                    src={post.image}
                    alt={post.title}
                    className="object-cover object-center"
                    style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                />
            </div>
            <div className="p-4 flex flex-col gap-3 flex-grow">
                <h3 className="text-xl font-exo text-rose font-semibold line-clamp-2">
                    <Link
                        to={`${postsBasePath}/${post.slug}`}
                        className="hover:text-primary"
                        onClick={() => setCurrentPost(post)}
                    >
                        {post.title}
                    </Link>
                </h3>
                <p className="text-sm text-gray-500">
                    {post.date} {post.heure && `- ${post.heure}`}
                </p>
                <p className="text-sm text-gray-700 line-clamp-2 flex-grow">{post.description}</p>
                <div className="mt-auto pt-3">
                    <Link
                        to={`${postsBasePath}/${post.slug}`}
                        onClick={() => setCurrentPost(post)}
                    >
                        <Button variant="outline" size="sm" className="w-full justify-between">
                            Lire l'article <ArrowRight size={14} />
                        </Button>
                    </Link>
                </div>
            </div>
        </div>
    );
}
